import { useToast } from "@src/appV2/lib";
import { useCallback, useEffect, useState } from "react";

import { useGetHostedTimeFacility } from "../api/useGetHostedTimeFacility";
import { useUpdateHostedTimeFacility } from "../api/useUpdateHostedTimeFacility";
import { useTu } from "@src/appV2/i18n/useTu";

interface UseHostedTimeParams {
  facilityId: string;
  facilityName: string;
  isCbhAdmin: boolean;
}

interface UseHostedTimeResult {
  deviceId: number;
  isLoading: boolean;
  isUpdating: boolean;
  onDeviceIdChange: (value: number) => void;
  onSave: () => void;
}

export function useHostedTime({
  facilityId,
  facilityName,
  isCbhAdmin,
}: UseHostedTimeParams): UseHostedTimeResult {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const { data: hostedTimeData, isLoading } = useGetHostedTimeFacility(
    { facilityId },
    { enabled: isCbhAdmin && Boolean(facilityId) }
  );

  const { isLoading: isUpdating, mutateAsync: updateHostedTime } = useUpdateHostedTimeFacility();

  const [deviceId, setDeviceId] = useState<number>(0);

  useEffect(() => {
    if (hostedTimeData !== undefined) {
      setDeviceId(hostedTimeData?.deviceId ?? 0);
    }
  }, [hostedTimeData]);

  const handleSave = useCallback(async () => {
    if (!facilityId || !facilityName) {
      return;
    }

    try {
      await updateHostedTime({
        facilityId,
        name: facilityName,
        deviceId,
      });
      showSuccessToast(tu("HostedTime device ID updated successfully"));
    } catch {
      showErrorToast(tu("Error updating HostedTime device ID. Please try again later."));
    }
  }, [facilityId, facilityName, deviceId, updateHostedTime, showSuccessToast, showErrorToast, tu]);

  return {
    deviceId,
    isLoading,
    isUpdating,
    onDeviceIdChange: setDeviceId,
    onSave: () => {
      void handleSave();
    },
  };
}
